import { useCallback, useState } from 'react';
import { useForm, type DefaultValues, type FieldValues, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ZodType } from 'zod';

import { applyApiError, type IFormRejection } from './api-errors';

export interface IApiForm<TValues extends FieldValues> {
  form: UseFormReturn<TValues>;
  submit: (event?: React.BaseSyntheticEvent) => Promise<void>;
  rejection: IFormRejection | null;
  /** For a form whose other action (a Check) proves the refusal stale before any resubmit. */
  clearRejection: () => void;
}

interface IUseApiFormArgs<TValues extends FieldValues> {
  schema: ZodType<TValues, TValues>;
  defaultValues: DefaultValues<TValues>;
  onSubmit: (values: TValues) => Promise<unknown>;
}

export const useApiForm = <TValues extends FieldValues>({
  schema,
  defaultValues,
  onSubmit,
}: IUseApiFormArgs<TValues>): IApiForm<TValues> => {
  const form = useForm<TValues>({
    resolver: zodResolver(schema),
    defaultValues,
    // Not `onTouched`: it reddens an autofocused field the moment the pointer moves, before anyone could fill it in.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const [rejection, setRejection] = useState<IFormRejection | null>(null);

  const submit = useCallback(
    (event?: React.BaseSyntheticEvent) => {
      setRejection(null);
      return form.handleSubmit(async (values) => {
        try {
          await onSubmit(values);
        } catch (error) {
          const applied = applyApiError(form, error);
          setRejection(applied.message ? applied : null);
        }
      })(event);
    },
    [form, onSubmit],
  );

  const clearRejection = useCallback(() => setRejection(null), []);

  return { form, submit, rejection, clearRejection };
};
